import { fmt } from '../lib/calendar'
import type { DayForecast, WeatherIcon } from '../lib/weather'

/**
 * The shape of the week (H3), and (WP 6.2) the weather.
 *
 * Seven cells from today: the weekday, the date, and Berlin's forecast for
 * it. It answers "is this week full of rain" before you have read a single
 * row, which nothing else on the screen does.
 *
 * **The strip shows weather, not dots.** H3's own accent dot for something of
 * Doug's own and a grey one for a pick are gone — `today.ts`'s `dayDots` went
 * with them. The prototype never carried either; a forecast the client fetches
 * itself is a fact the strip did not have before, where the dots were a
 * summary of lists the sections below already draw in full.
 *
 * Away days are shaded rather than removed. A week with Thursday to Sunday in
 * Wroclaw is still seven days long, and a strip that dropped them would put
 * next Monday where this Thursday is.
 */
export function DayStrip({ dates, forecast, away, today, selected, onPick }: {
  /** Seven calendar days, today first — `Today.tsx`'s own `addDaysIso`, not
   *  computed here: this component draws a week, it does not decide which
   *  one. */
  dates: string[]
  /** Open-Meteo's answer for a date, keyed by it. Absent entirely while the
   *  query is still pending, and missing a date the fetch failed to cover —
   *  either way the cell simply carries no glyph (the brief's "a failed fetch
   *  is a cell with no weather and never blocks the strip"). */
  forecast?: Map<string, DayForecast>
  /** Is he out of town that day? From `periods()`, which the screen already
   *  walks for the greeting. */
  away: (date: string) => boolean
  today: string
  /** The day Today is showing instead of its sections, or `null` when it is
   *  showing them (WP 5.2, item 2). Filled (a1-18, Doug's choice): the
   *  strongest state in the strip, where today is only a small dot —
   *  `.daycell.is-selected` and `.is-today` in `app.css`. */
  selected: string | null
  /** Select that day, or (the caller's job) clear the selection where it is
   *  already today's cell or the selected one. */
  onPick: (date: string) => void
}) {
  return (
    <div className="daystrip">
      {dates.map(date => {
        const isAway = away(date)
        const isSelected = date === selected
        const wx = forecast?.get(date)
        // What the cell is for, spoken: a screen reader gets "Saturday 26
        // September, away, cloudy, 16 degrees" rather than seven dates and a
        // row of pictures it cannot see.
        const label = [
          fmt(date, { weekday: 'long', day: 'numeric', month: 'long' }),
          isAway ? 'away' : null,
          wx ? `${WX_WORD[wx.icon]}, ${wx.max} degrees` : null,
        ].filter(Boolean).join(', ')

        return (
          <button
            key={date}
            type="button"
            className={`daycell${date === today ? ' is-today' : ''}${isAway ? ' is-away' : ''}${isSelected ? ' is-selected' : ''}`}
            aria-label={label}
            aria-current={date === today ? 'date' : undefined}
            aria-pressed={isSelected}
            onClick={() => onPick(date)}
          >
            <span className="daycell__dow">{fmt(date, { weekday: 'short' })}</span>
            <span className="daycell__num">{fmt(date, { day: 'numeric' })}</span>
            {/* Fixed height whether or not the fetch has answered for this
                date yet, so the seven cells stay one row of one height — the
                `.pitem` lesson: a row that sizes itself from what it happens
                to carry is a row that changes shape down the list. */}
            <span className="daycell__wx" aria-hidden="true">
              {wx && (
                <>
                  <WeatherGlyph icon={wx.icon} />
                  <b>{wx.max}°</b>
                </>
              )}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/** The plain-language word behind each glyph, for the cell's own `aria-label`
 *  — a screen reader gets the fact the glyph draws, not the glyph's name. */
const WX_WORD: Record<WeatherIcon, string> = {
  sun: 'sunny', part: 'partly cloudy', cloud: 'cloudy', rain: 'rain',
}

/** Four glyphs, one path each, `currentColor` throughout so light and dark
 *  need no second set — the prototype's own four (`WXI` in
 *  `docs/proto-plan/prototype/app.html`), redrawn as JSX rather than an
 *  innerHTML string. */
function WeatherGlyph({ icon }: { icon: WeatherIcon }) {
  return (
    <svg
      className="daycell__wxicon" width="13" height="13" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true"
    >
      {icon === 'sun' && (
        <>
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
        </>
      )}
      {icon === 'part' && (
        <>
          <path d="M12 3v1.5M5.6 5.6l1 1M3 12h1.5" />
          <circle cx="10" cy="10" r="3" />
          <path d="M17.5 19H9a3.5 3.5 0 1 1 .5-7A5 5 0 0 1 19 11a4 4 0 0 1-1.5 8z" />
        </>
      )}
      {icon === 'cloud' && (
        <path d="M17.5 19H7a4 4 0 1 1 .6-7.9A5.5 5.5 0 0 1 18 9.5a4.8 4.8 0 0 1-.5 9.5z" />
      )}
      {icon === 'rain' && (
        <>
          <path d="M16 13a4 4 0 0 0-.6-7.9A5.5 5.5 0 0 0 5 7.1 4 4 0 0 0 6 15h10" />
          <path d="M8 17l-1 3M12 17l-1 3M16 17l-1 3" />
        </>
      )}
    </svg>
  )
}
